/**
 * Binding one face to exactly one wallet.
 *
 * The requirement is that a person can walk up to any device, show their
 * face, type their passphrase, and be in their wallet - and that the same
 * face can never produce a second wallet. That needs a key that is a
 * deterministic function of the face, which a face alone cannot give you:
 * two scans of one person differ on roughly 12-14% of their code bits, and
 * a key that changes with the lighting is not a key.
 *
 * The fix is a *secure sketch*, published with the mint claim:
 *
 *   enrol    S  = 128 random bits          (the per-person recovery secret)
 *            W  = each bit of S repeated REPS times
 *            H  = W XOR faceSketchBits     (published; the "helper")
 *
 *   recover  W' = H XOR faceSketchBits'    = W XOR (bits XOR bits')
 *            S  = majority vote in each REPS-wide group
 *
 * Because the two scans differ in only a minority of positions, each group
 * of REPS still votes the right way, and S comes back bit-for-bit. A
 * stranger's bits differ on about half the positions, so their groups vote
 * at random and S comes back wrong - which the published commitment
 * detects immediately.
 *
 * BE CLEAR ABOUT WHAT THIS PROTECTS. The helper is public, and so is the
 * face code it was masked with, so anyone holding a photograph of you can
 * recover S. S is an *identifier*, not a secret: it says which wallet, not
 * whether you may open it. The passphrase is the only secret, which is
 * exactly why it is mandatory and why the derivation that consumes it is
 * deliberately slow (see deriveWalletSeed in keys.ts). A face is something
 * you cannot change and cannot hide; building a system that treats one as
 * a password would be indefensible.
 */

import { randomBytes, toHex, type Hex } from './bytes.ts';
import { tag } from './hash.ts';

/** Length of the per-person recovery secret. */
export const SECRET_BITS = 128;
export const SECRET_BYTES = SECRET_BITS / 8;

/**
 * Repetitions per secret bit - how much scan-to-scan drift survives.
 *
 * Measured on the reference implementation: 32 recovers 100% of the time
 * up to 17% bit drift and degrades gracefully past 25%, where a real face
 * never lands. 24 was enough for the common case but lost half a percent
 * of people at the far end of the lighting range, and half a percent of
 * people locked out of their money is not an acceptable saving of 128
 * bytes per claim.
 */
export const SKETCH_REPS = 32;

/** Total helper length. */
export const SKETCH_BITS = SECRET_BITS * SKETCH_REPS; // 3072
export const SKETCH_BYTES = SKETCH_BITS / 8; // 384

const bit = (buf: Uint8Array, i: number): number => (buf[i >> 3] >> (i & 7)) & 1;
const setBit = (buf: Uint8Array, i: number): void => {
  buf[i >> 3] |= 1 << (i & 7);
};

/** Spread each secret bit across its REPS positions. */
function expand(secret: Uint8Array): Uint8Array {
  const out = new Uint8Array(SKETCH_BYTES);
  for (let s = 0; s < SECRET_BITS; s++) {
    if (!bit(secret, s)) continue;
    const base = s * SKETCH_REPS;
    for (let r = 0; r < SKETCH_REPS; r++) setBit(out, base + r);
  }
  return out;
}

export interface Sketch {
  /** Public helper, safe to publish. */
  helper: Hex;
  /** Commitment to the secret, so a decode can be checked before use. */
  commit: Hex;
}

/**
 * Build the helper for a freshly enrolled face.
 * @param sketchBits SKETCH_BITS of face-derived bits (see faceSketchBits)
 */
export function enrollSketch(sketchBits: Uint8Array, secret = randomBytes(SECRET_BYTES)): { secret: Uint8Array; sketch: Sketch } {
  if (sketchBits.length !== SKETCH_BYTES) throw new Error(`sketch input must be ${SKETCH_BYTES} bytes`);
  if (secret.length !== SECRET_BYTES) throw new Error(`secret must be ${SECRET_BYTES} bytes`);
  const w = expand(secret);
  const helper = new Uint8Array(SKETCH_BYTES);
  for (let i = 0; i < SKETCH_BYTES; i++) helper[i] = w[i] ^ sketchBits[i];
  return { secret, sketch: { helper: toHex(helper), commit: toHex(secretCommitment(secret)) } };
}

/** Public commitment to a recovery secret. Not brute-forceable: S is 128 random bits. */
export function secretCommitment(secret: Uint8Array): Uint8Array {
  return tag('face/sketch/commit/v1', secret);
}

export interface RecoveryResult {
  ok: boolean;
  secret?: Uint8Array;
  /**
   * Mean margin of the majority votes, 0 to 1: 1 means every group agreed
   * unanimously, 0 means they were coin flips. Counting *unanimous* groups
   * instead would read as 1% on a perfectly good scan, because ordinary
   * drift flips at least one position in nearly every group - a number
   * that alarms people about a recovery that in fact succeeded.
   */
  confidence: number;
  reason?: string;
}

/**
 * Recover the secret from a fresh scan plus the published helper.
 *
 * The commitment check is what makes a wrong answer impossible to act on:
 * a different person, or a scan too poor to decode, produces a secret that
 * does not match and is rejected here rather than silently deriving some
 * other wallet.
 */
export function recoverSecret(sketchBits: Uint8Array, helperHex: Hex, commitHex: Hex): RecoveryResult {
  if (sketchBits.length !== SKETCH_BYTES) return { ok: false, confidence: 0, reason: 'bad sketch length' };
  let helper: Uint8Array;
  try {
    helper = hexToBytes(helperHex);
  } catch {
    return { ok: false, confidence: 0, reason: 'malformed helper' };
  }
  if (helper.length !== SKETCH_BYTES) return { ok: false, confidence: 0, reason: 'malformed helper' };

  const w = new Uint8Array(SKETCH_BYTES);
  for (let i = 0; i < SKETCH_BYTES; i++) w[i] = helper[i] ^ sketchBits[i];

  const secret = new Uint8Array(SECRET_BYTES);
  let marginSum = 0;
  for (let s = 0; s < SECRET_BITS; s++) {
    const base = s * SKETCH_REPS;
    let ones = 0;
    for (let r = 0; r < SKETCH_REPS; r++) ones += bit(w, base + r);
    if (ones * 2 > SKETCH_REPS) setBit(secret, s);
    marginSum += Math.abs(2 * ones - SKETCH_REPS) / SKETCH_REPS;
  }
  const confidence = marginSum / SECRET_BITS;

  if (toHex(secretCommitment(secret)) !== commitHex) {
    return { ok: false, confidence, reason: 'the scan did not reproduce this wallet' };
  }
  return { ok: true, secret, confidence };
}

function hexToBytes(h: Hex): Uint8Array {
  if (typeof h !== 'string' || h.length % 2 !== 0) throw new Error('bad hex');
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) {
    const v = parseInt(h.substr(i * 2, 2), 16);
    if (Number.isNaN(v)) throw new Error('bad hex');
    out[i] = v;
  }
  return out;
}
